/**
 * The ONE next step on Home — the first step of the journey the server says is
 * neither done nor locked, in the order the stepper draws them. Web's hero and
 * the phone's hero both ask this, so the two can never point at different
 * places (the phone ran its own ranking that put Care before TelePortal, and
 * took a locked Care for "not started" — 3 Oct).
 *
 * Which step, and whether it is started; the label key comes with it, the route
 * is each app's own.
 *
 * @module heroes/home
 */
import type { HomeStep, HomeStepKey } from '@tonnie/types';

export type JourneyNextStepKey = Exclude<HomeStepKey, 'start'>;

/** The call to action's words, per step and whether it is under way. */
export const JOURNEY_STEP_CTA_KEYS = {
  checkup: { start: 'dashboard.cta.startCheckUp', continue: 'dashboard.cta.viewCheckUp' },
  teleportal: {
    start: 'dashboard.cta.startTelePortal',
    continue: 'dashboard.cta.continueTelePortal',
  },
  qultureup: {
    start: 'dashboard.cta.startQultureUp',
    continue: 'dashboard.cta.continueQultureUp',
  },
  care: { start: 'dashboard.cta.startCare', continue: 'dashboard.cta.continueCare' },
  passport: { start: 'dashboard.cta.viewPassport', continue: 'dashboard.cta.viewPassport' },
} as const satisfies Record<JourneyNextStepKey, { start: string; continue: string }>;

export interface JourneyNextStep {
  step: JourneyNextStepKey;
  labelKey: (typeof JOURNEY_STEP_CTA_KEYS)[JourneyNextStepKey]['start' | 'continue'];
}

export function journeyNextStep(steps: readonly HomeStep[]): JourneyNextStep {
  const open = steps.find(
    (s): s is HomeStep & { key: JourneyNextStepKey } =>
      s.key !== 'start' && s.status !== 'completed' && !s.locked
  );
  // Every open step done: the journey's record is the Passport.
  const step: JourneyNextStepKey = open?.key ?? 'passport';
  const keys = JOURNEY_STEP_CTA_KEYS[step];
  return { step, labelKey: open?.status === 'in_progress' ? keys.continue : keys.start };
}
